import React, { useState } from 'react';
import { WeighingTicketData } from '@/components/WeighingTicket';
import EmailRecipientSelector from '@/components/EmailRecipientSelector';
import ticketActionsService from '@/services/ticketActionsService';

interface EmailModalProps {
  ticketData: WeighingTicketData;
  emailData: {
    recipients: string;
    subject: string;
    message: string;
    cc: string;
    bcc: string;
  };
  onEmailDataChange: (data: any) => void;
  onSend: () => void;
  onClose: () => void;
}

const EmailModal: React.FC<EmailModalProps> = ({
  ticketData,
  emailData,
  onEmailDataChange,
  onSend,
  onClose
}) => {
  const [selectedActors, setSelectedActors] = useState<any[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  const [manualRecipients, setManualRecipients] = useState('');

  const handleInputChange = (field: string, value: string) => {
    onEmailDataChange({
      ...emailData,
      [field]: value
    });
  };

  const buildRecipientsList = (): string => {
    const recipients: string[] = [];
    
    selectedActors.forEach(actor => {
      if (actor.email) {
        recipients.push(actor.email);
      }
    });
    
    selectedUsers.forEach(user => {
      if (user.email) {
        recipients.push(user.email);
      }
    });
    
    if (manualRecipients.trim()) {
      const manualEmails = manualRecipients
        .split(',')
        .map(email => email.trim())
        .filter(email => email.length > 0);
      recipients.push(...manualEmails);
    }
    
    return recipients.join(', ');
  };

  const handleSendEmail = async () => {
    try {
      const allRecipients = buildRecipientsList();
      
      if (!allRecipients.trim()) {
        alert('Veuillez sélectionner au moins un destinataire ou saisir une adresse email.');
        return;
      }

      // Convertir les destinataires en tableaux
      const recipientsList = allRecipients
        .split(',')
        .map(email => email.trim())
        .filter(email => email.length > 0);
      
      const ccList = emailData.cc
        .split(',')
        .map(email => email.trim())
        .filter(email => email.length > 0);
      
      const bccList = emailData.bcc
        .split(',')
        .map(email => email.trim())
        .filter(email => email.length > 0);

      // Afficher un message de chargement
      const loadingMessage = document.createElement('div');
      loadingMessage.innerHTML = `
        <div style="position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); 
                    background: white; padding: 20px; border-radius: 8px; box-shadow: 0 4px 6px rgba(0,0,0,0.1);
                    z-index: 10001; text-align: center;">
          <div style="margin-bottom: 10px;">
            <i class="fas fa-spinner fa-spin" style="font-size: 24px; color: #3B82F6;"></i>
          </div>
          <p style="margin: 0; color: #374151;">Génération du PDF et envoi de l'email en cours...</p>
        </div>
      `;
      document.body.appendChild(loadingMessage);

      // Envoyer l'email via l'API backend avec le HTML généré
      await ticketActionsService.sendTicketEmailFromApp(
        ticketData,
        recipientsList,
        ccList,
        bccList,
        emailData.subject,
        emailData.message,
        {
          onSuccess: () => {
            document.body.removeChild(loadingMessage);
            alert('✅ PDF généré et email envoyé avec succès !');
            onSend();
          },
          onError: (error) => {
            document.body.removeChild(loadingMessage);
            alert(`❌ Erreur lors de l'envoi de l'email: ${error}`);
          }
        }
      );
      
    } catch (error) {
      console.error('❌ Erreur lors de l\'envoi d\'email:', error);
      alert('❌ Erreur lors de l\'envoi d\'email. Veuillez réessayer.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Envoyer le ticket par email</h2>
              <p className="text-sm text-gray-600 mt-1">Le PDF avec logos sera généré et envoyé directement depuis le serveur</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <i className="fas fa-times text-xl"></i>
            </button>
          </div>

          <div className="space-y-6">
            {/* Sélection des destinataires */}
            <EmailRecipientSelector
              weighingDetails={ticketData as any}
              selectedActors={selectedActors}
              selectedUsers={selectedUsers}
              manualRecipients={manualRecipients}
              onActorsChange={setSelectedActors}
              onUsersChange={setSelectedUsers}
              onManualRecipientsChange={setManualRecipients}
            />

            {/* CC */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="fas fa-copy text-gray-500 mr-2"></i>
                Copie (CC)
              </label>
              <input
                type="email"
                value={emailData.cc}
                onChange={(e) => handleInputChange('cc', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="cc@email.com"
              />
            </div>

            {/* BCC */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="fas fa-eye-slash text-gray-500 mr-2"></i>
                Copie cachée (BCC)
              </label>
              <input
                type="email"
                value={emailData.bcc}
                onChange={(e) => handleInputChange('bcc', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="bcc@email.com"
              />
            </div>

            {/* Sujet */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="fas fa-tag text-green-500 mr-2"></i>
                Sujet
              </label>
              <input
                type="text"
                value={emailData.subject}
                onChange={(e) => handleInputChange('subject', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Sujet de l'email"
              />
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="fas fa-comment text-purple-500 mr-2"></i>
                Message
              </label>
              <textarea
                value={emailData.message}
                onChange={(e) => handleInputChange('message', e.target.value)}
                rows={8}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Votre message..."
              />
              <p className="mt-1 text-xs text-gray-500">
                Le ticket PDF avec logos sera automatiquement généré et joint à l'email par le serveur
              </p>
            </div>

            {/* Informations sur le ticket */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">Informations du ticket</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Référence:</span>
                  <p className="font-medium">{ticketData.code}</p>
                </div>
                <div>
                  <span className="text-gray-600">Produit:</span>
                  <p className="font-medium">{ticketData.product}</p>
                </div>
                <div>
                  <span className="text-gray-600">Poids net:</span>
                  <p className="font-medium">{ticketData.netWeight.toLocaleString()} kg</p>
                </div>
                <div>
                  <span className="text-gray-600">Date:</span>
                  <p className="font-medium">{ticketData.weighingDate || new Date().toLocaleDateString('fr-FR')}</p>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-600">Destinataires sélectionnés:</span>
                  <p className="font-medium text-blue-600">
                    {buildRecipientsList().split(',').length} destinataire(s)
                  </p>
                </div>
              </div>
            </div>
          </div>

          

          {/* Boutons d'action */}
          <div className="flex justify-between items-center mt-8">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Annuler
            </button>
            
            <button
              onClick={handleSendEmail}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center"
            >
              <i className="fas fa-paper-plane mr-2"></i>
              Envoyer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailModal;
